import React, { useState } from "react";
import {
  Box,
  Heading,
  Text,
  Container,
  VStack,
  Input,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Link,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { changePassword } from "../api/auth";

export default function ChangePasswordPage() {
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const currentPasswordError = !currentPassword
    ? "Current password is required."
    : "";

  const newPasswordError = !newPassword
    ? "New password is required."
    : newPassword.length < 8
      ? "New password must be at least 8 characters."
      : newPassword === currentPassword
        ? "New password must be different from current password."
        : "";

  const confirmPasswordError = !confirmNewPassword
    ? "Please confirm your new password."
    : newPassword !== confirmNewPassword
      ? "Passwords do not match."
      : "";

  async function onSubmit(e) {
    e.preventDefault();
    setApiError("");
    setSuccessMessage("");

    if (currentPasswordError || newPasswordError || confirmPasswordError) {
      return;
    }

    try {
      setSubmitting(true);

      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });

      setSuccessMessage("Password changed successfully. Redirecting to home...");

      setTimeout(() => {
        navigate("/", { replace: true });
      }, 1200);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Unable to change password.";

      setApiError(typeof msg === "string" ? msg : "Unable to change password.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box minH="100vh" bg="#00007f" color="white">
      <Box bg="#00005f" borderBottom="2px solid #7a7a7a" boxShadow="md">
        <Container maxW="container.xl" py={5}>
          <Heading
            fontFamily="'Orbitron', sans-serif"
            fontSize="40px"
            letterSpacing="3px"
            fontWeight="600"
          >
            The Deep End
          </Heading>
        </Container>
      </Box>

      <Container maxW="container.md" py={12}>
        <VStack spacing={8} align="stretch">
          <VStack spacing={2} align="start">
            <Heading size="lg">Change Password</Heading>
            <Text color="whiteAlpha.900">
              Update your password for your account.
            </Text>
          </VStack>

          <Box
            as="form"
            onSubmit={onSubmit}
            bg="white"
            color="black"
            border="3px solid #00005f"
            borderRadius="lg"
            boxShadow="lg"
            px={6}
            py={8}
          >
            <VStack spacing={4}>
              {apiError ? (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {apiError}
                </Alert>
              ) : null}

              {successMessage ? (
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  {successMessage}
                </Alert>
              ) : null}

              <FormControl isInvalid={!!currentPasswordError && currentPassword.length > 0}>
                <FormLabel>Current Password</FormLabel>
                <Input
                  placeholder="Current Password"
                  type="password"
                  bg="white"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <FormErrorMessage>{currentPasswordError}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!newPasswordError && newPassword.length > 0}>
                <FormLabel>New Password</FormLabel>
                <Input
                  placeholder="New Password"
                  type="password"
                  bg="white"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <FormErrorMessage>{newPasswordError}</FormErrorMessage>
              </FormControl>

              <FormControl
                isInvalid={!!confirmPasswordError && confirmNewPassword.length > 0}
              >
                <FormLabel>Confirm New Password</FormLabel>
                <Input
                  placeholder="Confirm New Password"
                  type="password"
                  bg="white"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <FormErrorMessage>{confirmPasswordError}</FormErrorMessage>
              </FormControl>

              <Button
                type="submit"
                bg="#00005f"
                color="white"
                w="100%"
                isLoading={submitting}
                loadingText="Saving"
                _hover={{ bg: "#00004a" }}
              >
                Save New Password
              </Button>

              <Text fontSize="sm">
                <Link as={RouterLink} to="/" color="#00005f" fontWeight="600">
                  Back to Home
                </Link>
              </Text>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}